import React from "react";
import axios from "axios";
import "./index.css";
import { BrowserRouter, Routes, Route, Navigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";
import { AuthProvider } from "./contexts/AuthContext";
import { ClassroomProvider } from "./contexts/ClassroomContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SoundProvider } from "./hooks/useSound";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { ProgramsGate } from "./components/ProgramsGate";
import { TeacherLayout } from "./components/TeacherLayout";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { Settings } from "./pages/Settings";
import { PlatformAdmin } from "./pages/PlatformAdmin";
import { BrowseHome } from "./pages/BrowseHome";
import { MonthSelect } from "./pages/MonthSelect";
import { LevelTopics } from "./pages/LevelTopics";
import { TopicDetail } from "./pages/TopicDetail";
import { UnifiedGameSelect } from "./pages/UnifiedGameSelect";
import { UnifiedGameSetup } from "./pages/UnifiedGameSetup";
import { UnifiedGamePlay } from "./pages/UnifiedGamePlay";
import TeachFirst from "./pages/TeachFirst";
import { Curriculum } from "./pages/Curriculum";
import { PlaylistDetail } from "./pages/PlaylistDetail";
import { SamplePlaylistDetail } from "./pages/SamplePlaylistDetail";
import { TeacherGuide } from "./pages/TeacherGuide";
import { TeacherFaq } from "./pages/TeacherFaq";
import { ProgramCatalog } from "./pages/ProgramCatalog";
import { ProgramDetail } from "./pages/ProgramDetail";
import { LessonPlayer } from "./pages/LessonPlayer";
import Subscribe from "./pages/Subscribe";
import LandingPage from "./pages/LandingPage";
import InviteLanding from "./pages/InviteLanding";
import { OnboardingTour } from "./components/OnboardingTour";
import { useInviteTracking } from "./hooks/useInviteTracking";

// Auth uses httponly, samesite=none, secure cookies. Browsers (Chrome/Safari)
// silently drop those when frontend and backend are on different eTLD+1s
// because of third-party cookie blocking. To stay durably same-origin, when
// the JS is running on the canonical production host, talk to the backend at
// the same origin (the platform proxies /api/* through frenchboard.ca).
// Anywhere else (preview, localhost), fall back to REACT_APP_BACKEND_URL.
const PROD_HOSTS = new Set(["frenchboard.ca", "www.frenchboard.ca"]);
const RUNTIME_HOST = typeof window !== "undefined" ? window.location.hostname : "";
const IS_PROD_HOST = PROD_HOSTS.has(RUNTIME_HOST);
const BACKEND_URL = IS_PROD_HOST
  ? window.location.origin
  : process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Still required for the preview / dev environments where frontend and backend
// live on different hosts. Harmless when same-origin.
axios.defaults.withCredentials = true;

// SAFETY NET: 16 files independently build their own API URL from
// process.env.REACT_APP_BACKEND_URL, which on the production bundle points at
// the preview host (cross-origin → cookies dropped). On the canonical prod
// host we rewrite any outbound URL that targets the env-baked backend so it
// resolves to same-origin instead. Single source of truth; no per-file edits.
if (IS_PROD_HOST && process.env.REACT_APP_BACKEND_URL) {
  const envBackend = process.env.REACT_APP_BACKEND_URL.replace(/\/$/, "");
  axios.interceptors.request.use((config) => {
    if (typeof config.url === "string" && config.url.startsWith(envBackend)) {
      config.url = window.location.origin + config.url.slice(envBackend.length);
    }
    return config;
  });
}

/* Smart root: landing page for visitors, redirect for logged-in users */
function SmartRoot() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/browse" replace />;
  return <LandingPage />;
}

function App() {
  return (
    <SoundProvider>
      <BrowserRouter>
        <AuthProvider>
          <LanguageProvider>
          <ClassroomProvider>
          <AppShell />
          </ClassroomProvider>
          </LanguageProvider>
        </AuthProvider>
      </BrowserRouter>
    </SoundProvider>
  );
}

/** Inner shell so we can use auth-context hooks (tracking + onboarding overlay) */
function AppShell() {
  useInviteTracking();
  return (
    <div style={{ height: '100vh', overflow: 'hidden', background: '#F8F9FB' }}>
      <OnboardingTour />
      <Routes>
        {/* Public pages */}
        <Route path="/" element={<SmartRoot />} />
        <Route path="/login" element={<Login />} />
        <Route path="/subscribe/:token" element={<Subscribe />} />
        {/* Invite-link magic entry */}
        <Route path="/try/:slug" element={<InviteLanding />} />

        <Route element={<ProtectedRoute />}>
          {/* Pages with sidebar */}
          <Route element={<TeacherLayout />}>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/browse" element={<BrowseHome />} />
            <Route path="/school/:grade" element={<MonthSelect />} />
            <Route path="/level/:level" element={<LevelTopics />} />
            <Route path="/topic/:topic" element={<TopicDetail />} />
            <Route path="/games" element={<UnifiedGameSelect />} />
            <Route path="/play/:game/setup" element={<UnifiedGameSetup />} />
            <Route path="/play/:game/teach" element={<TeachFirst />} />
            <Route path="/progress" element={<Curriculum />} />
            <Route path="/curriculum" element={<Navigate to="/progress" replace />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/admin" element={<PlatformAdmin />} />
            <Route path="/playlists/:id" element={<PlaylistDetail />} />
            <Route path="/samples/:id" element={<SamplePlaylistDetail />} />
            <Route path="/guide" element={<TeacherGuide />} />
            <Route path="/faq" element={<TeacherFaq />} />
            <Route element={<ProgramsGate />}>
              <Route path="/programs" element={<ProgramCatalog />} />
              <Route path="/programs/:programId" element={<ProgramDetail />} />
              <Route path="/programs/:programId/lessons/:lessonId" element={<LessonPlayer />} />
            </Route>
          </Route>

          {/* Game play — fullscreen, no sidebar */}
          <Route path="/play/:game" element={
            <div style={{ height: '100%', position: 'relative' }}>
              <UnifiedGamePlay />
            </div>
          } />
        </Route>

        {/* Legacy redirects */}
        <Route path="/grade/:grade" element={<LegacyGradeRedirect />} />
        <Route path="/grade/:grade/month/:month" element={<LegacyMonthRedirect />} />
        <Route path="/grade/:grade/month/:month/game/:game/setup" element={<LegacySetupRedirect />} />
        <Route path="/grade/:grade/month/:month/game/:game/play" element={<LegacyPlayRedirect />} />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

function LegacyGradeRedirect() {
  const { grade } = useParams();
  return <Navigate to={`/school/${grade}`} replace />;
}
function LegacyMonthRedirect() {
  const { grade, month } = useParams();
  return <Navigate to={`/games?mode=school&grade=${grade}&month=${month}`} replace />;
}
function LegacySetupRedirect() {
  const { grade, month, game } = useParams();
  return <Navigate to={`/play/${game}/setup?mode=school&grade=${grade}&month=${month}`} replace />;
}
function LegacyPlayRedirect() {
  const { grade, month, game } = useParams();
  const [searchParams] = useSearchParams();
  return <Navigate to={`/play/${game}?mode=school&grade=${grade}&month=${month}&${searchParams}`} replace />;
}

export default App;
